import { ArrowRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import type { BudgetReviewItem, BudgetReviewResolution } from "@/schemas";

import { EmptyStateCard } from "./empty-state-card";
import { ReviewCandidateCard } from "./review-candidate-card";

export function BudgetReviewQueueCard({
  reviews,
  currencyCode,
  onResolve,
}: {
  reviews: BudgetReviewItem[];
  currencyCode: string;
  onResolve: (reviewId: string, resolution: BudgetReviewResolution) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review Queue</CardTitle>
        <CardDescription>
          Resolve ambiguous matches from imports.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {reviews.length ? (
          reviews.map((review) => (
            <div key={review.id} className="rounded-lg border p-4">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm font-medium">Review Match</p>
                <Badge
                  variant={review.status === "open" ? "default" : "secondary"}
                >
                  {review.status}
                </Badge>
              </div>

              <div className="space-y-3">
                <ReviewCandidateCard
                  title="Expected"
                  candidate={review.auto_candidate}
                  currencyCode={currencyCode}
                />
                <div className="flex justify-center">
                  <ArrowRight className="h-4 w-4 rotate-90 text-muted-foreground" />
                </div>
                <ReviewCandidateCard
                  title="Imported"
                  candidate={review.import_candidate}
                  currencyCode={currencyCode}
                />
              </div>

              {review.status === "open" && (
                <div className="mt-4 flex flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      void onResolve(review.id, "merge_to_budget")
                    }
                  >
                    Merge expected & import
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        void onResolve(review.id, "keep_import")
                      }
                    >
                      Keep import
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        void onResolve(review.id, "keep_budget")
                      }
                    >
                      Keep expected
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <EmptyStateCard text="No pending reviews." />
        )}
      </CardContent>
    </Card>
  );
}
